import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'

import CompanyFcaServiceDetailsRow from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/components/CompanyFcaServiceDetailsRow'
import OtherServicesField from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/components/OtherServicesField'
import { ServiceFormValues } from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/types'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  useCurrentCompanyServicesQuery,
  useInvalidateCurrentCompanyServicesQuery,
} from '@/hooks/useCompanyServicesQuery'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { faAdd } from '@/lib/fontAwesomePro/solid'
import { cn } from '@/lib/utils'
import { CompanyItem } from '@/types/responses/companies'

interface Props {
  company: CompanyItem
  isIfaFirm: boolean
  onUpdate?: () => void
}

const CompanyFcaServices: React.FC<Props> = ({
  company,
  isIfaFirm,
  onUpdate,
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const [newService, setNewService] = useState<ServiceFormValues | null>(null)
  const invalidateCompanyServicesQuery =
    useInvalidateCurrentCompanyServicesQuery()

  const {
    isLoading: companyServicesQueryIsLoading,
    data: companyServicesData,
  } = useCurrentCompanyServicesQuery()

  if (companyServicesQueryIsLoading || !companyServicesData) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const companyServices = companyServicesData['hydra:member']

  function hasServices() {
    return !!companyServices && companyServices.length > 0
  }

  return (
    <section className="pop-in">
      <div className="flex flex-col items-stretch gap-4">
        {companyServices.map((service, index) => (
          <CompanyFcaServiceDetailsRow
            key={`${index}-${service.id}`}
            isEditingByDefault={false}
            service={{
              id: service.id,
              company: currentCompany['@id'],
              serviceArea: service.serviceArea,
              isOnlyInConjunctionWithFP: !service.isStandaloneService,
              notes: service.notes ?? '',
              serviceType: service.serviceType,
            }}
            onSave={() => {
              invalidateCompanyServicesQuery()

              if (onUpdate) {
                onUpdate()
              }
            }}
            onDelete={() => {
              invalidateCompanyServicesQuery()
            }}
          />
        ))}

        {newService && (
          <CompanyFcaServiceDetailsRow
            key="new"
            isEditingByDefault={true}
            service={newService}
            // We're done with the new one now - it's in the collection
            onSave={async () => {
              await invalidateCompanyServicesQuery()

              setNewService(null)

              if (onUpdate) {
                onUpdate()
              }
            }}
            onDelete={() => setNewService(null)}
          />
        )}
      </div>

      {!newService && (
        <div
          className={cn(
            'flex justify-center',
            hasServices() ? 'mt-4' : 'mb-12',
          )}
        >
          <Button
            onClick={() =>
              setNewService({
                id: undefined,
                company: currentCompany['@id'],
                serviceArea: undefined,
                isOnlyInConjunctionWithFP: false,
                notes: '',
                serviceType: undefined,
              })
            }
            variant={hasServices() ? 'link' : 'success'}
            size="lg"
          >
            <FontAwesomeIcon icon={faAdd} />
            {hasServices() ? 'Add another service' : 'Add a service'}
          </Button>
        </div>
      )}

      <OtherServicesField company={company} isIfaFirm={isIfaFirm} />
    </section>
  )
}

export default CompanyFcaServices
